import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonModal,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonNote,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonToast,
  IonAlert,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import {
  getDeviceGraphs,
  getSIMDetails,
  searchDevices,
  sendCalibrationData,
  updateDeviceDetails,
  updateSensorId,
  activateSIM,
  suspendSIM,
} from "../services/device.service";
import TimeSeriesChart from "./TimeSeriesChart";
import TimeRangeSelector from "./TimeRangeSelector";
import { FINAL_FIELD_DATA_LIMIT, TimeRangeSelection, resolveTimeRange } from "../utilities/timeRange";
import { getChartData } from "./PlotSensorData";
import { closeCircleOutline } from "ionicons/icons";
import { createFieldExtractor } from "../utilities/utils";
import _ from "lodash";
import { getSoilTypes } from "../services/plot.service";
import SelectItem from "./SelectItem";

type CalibrationData = {
  [key: string]: { [field: string]: string | number };
};
type PulseCalibrationData = {
  [key: string]: string | number;
};

const DeviceGraphs: React.FC = () => {
  const location = useLocation();
  const [deviceData, setDeviceData] = useState<any>();
  const [deviceGraphs, setDeviceGraphs] = useState<any[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [timeRange, setTimeRange] = useState<TimeRangeSelection>({ mode: "sinceHours", sinceHours: 240 });
  const [ccid, setCcid] = useState<string>('');
  const [sensorId, setSensorId] = useState<string>('');
  const [onSuccess, setOnSuccess] = useState("");
  const [onError, setOnError] = useState("");
  const [soilTypes, setSoilTypes] = useState<any[]>([])
  const [showSelectSoilTypeModal, setShowSelectSoilTypeModal] = useState(false);
  const [selectedMoistureForModal, setSelectedMoistureForModal] = useState('');


  const [device, setDevice] = useState({ id: "", imei: "" });
  const [showSIMAlert, setShowSIMAlert] = useState(false);
  const [simStatus, setSimStatus] = useState<string>('');
  const [simDetails, setSimDetails] = useState<Record<string, any> | null>(null);
  const [isLoadingSIM, setIsLoadingSIM] = useState(false);

  useEffect(() => {
    if (location.state) {
      const locationState = location.state as any;
      const deviceId = locationState.deviceId ?? "";
      const imei = locationState.imei ?? "";
      fetchSoilTypes();
      if (deviceId) {
        getGraphs(deviceId);
        getDeviceData(deviceId);
        setDevice({
          id: deviceId,
          imei,
        });
      }
    }
  }, [location]);

  useEffect(() => {
    if (!isLoading) {
      getGraphs(device.id, timeRange);
    }
  }, [timeRange]);

  const getDeviceData = async (deviceId: string) => {
    const devices = await searchDevices(deviceId, 0, 10);
    setDeviceData(devices[0]);
    if (devices[0]?.calibration) {
      if (['NERO_PULSE_UNIT', 'NERO_PULSE_LITE_UNIT'].includes(devices[0].deviceType)) {
        setDefaultPulseCalibrationData(_.merge({}, defaultPulseCalibrationData, { calibration: devices[0]?.calibration }));
      } else {
        setDefaultCalibrationData(_.merge({}, defaultCalibrationData, { calibration: devices[0]?.calibration }));
      }
    }
    if (devices[0]?.ccid) {
      setCcid(devices[0]?.ccid);
    }
    if (devices[0]?.sensorId) {
      setSensorId(devices[0]?.sensorId);
    }
  };

  const getGraphs = async (deviceId: string, range: TimeRangeSelection = timeRange) => {
    setIsLoading(true);
    try {
      const { start, end } = resolveTimeRange(range);
      const graphs = await getDeviceGraphs(deviceId, start, end);
      setDeviceGraphs(graphs);
    } catch (err: any) {
      setDeviceGraphs([]);
      setOnError(err?.message || "Failed to fetch device graphs");
    } finally {
      setIsLoading(false);
    }
  };


  const extractNumericFields = createFieldExtractor("number");
  const priorityFields = ['I1', 'I2', 'moisture1', 'soilMoisture1', 'moisture2', 'soilMoisture2'];
  const allFields = extractNumericFields(deviceGraphs ?? []);
  const fields = [
    ...priorityFields.filter((f) => allFields.includes(f)),
    ...allFields.filter((f) => !priorityFields.includes(f)),
  ];


  const [defaultCalibrationData, setDefaultCalibrationData] = useState<{
    calibration: CalibrationData;
  }>({
    calibration: {
      moisture1: { air: '', water: '', soil: '', soilType: '', soilMax: '' },
      moisture2: { air: '', water: '', soil: '', soilType: '', soilMax: '' },
      moisture3: { air: '', water: '', soil: '', soilType: '', soilMax: '' },
      moisture4: { air: '', water: '', soil: '', soilType: '', soilMax: '' },
    },
  });

  const [defaultPulseCalibrationData, setDefaultPulseCalibrationData] = useState<{
    calibration: PulseCalibrationData;
  }>({
    calibration: {
      lle: '',
      ree: '',
      llf: '',
      ref: '',
      rle: '',
      rlf: ''
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editableData, setEditableData] = useState<CalibrationData>({});
  const [editablePulseData, setEditablePulseData] = useState<PulseCalibrationData>({});

  const handleOpenModal = () => {
    if (['NERO_PULSE_UNIT', 'NERO_PULSE_LITE_UNIT'].includes(deviceData?.deviceType)) {
      setEditablePulseData({ ...defaultPulseCalibrationData.calibration });
      setIsModalOpen(true);
    } else {
      setEditableData({ ...defaultCalibrationData.calibration });
      setIsModalOpen(true);
    }
  };

  const handleFieldChange = (moisture: string, key: string, value: string) => {
    setEditableData((prev) => ({
      ...prev,
      [moisture]: {
        ...prev[moisture],
        [key]: value,
      },
    }));
  };

  const handlePulseFieldChange = (key: string, value: string) => {
    setEditablePulseData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    const deviceId = device.id;
    try {
      const response = await sendCalibrationData(deviceId, ['NERO_PULSE_UNIT', 'NERO_PULSE_LITE_UNIT'].includes(deviceData?.deviceType) ? editablePulseData : editableData);

      if (response.success) {
        if (['NERO_PULSE_UNIT', 'NERO_PULSE_LITE_UNIT'].includes(deviceData?.deviceType)) {
          setDefaultPulseCalibrationData((prev) => ({
            ...prev,
            calibration: editablePulseData,
          }));
        } else {
          setDefaultCalibrationData((prev) => ({
            ...prev,
            calibration: editableData,
          }));
        }
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error saving calibration data", error);
    }
  };

  const updateDevice = async () => {
    const deviceId = device.id;
    try {
      const response = await updateDeviceDetails(deviceId, { ccid });
      if (response.success) {
        setOnSuccess(response.message!);
      } else {
        setOnError(response.message!);
      }
    } catch (error) {
      setOnError('Some error occured');
    }
  };

  const updateDeviceSensorId = async () => {
    const deviceId = device.id;
    const imei = device.imei;
    try {
      const response = await updateSensorId(deviceId, sensorId, imei);
      if (response.success) {
        setOnSuccess(response.message!);
      } else {
        setOnError(response.message!);
      }
    } catch (error) {
      setOnError('Some error occurred while updating sensorId');
    }
  };

  const handleClearField = (moisture: string, key: string) => {
    setEditableData((prevData) => ({
      ...prevData,
      [moisture]: {
        ...prevData[moisture],
        [key]: '',
      },
    }));
  };

  const handlePulseClearField = (key: string) => {
    setEditablePulseData((prev) => ({
      ...prev,
      [key]: '',
    }));
  };

  const fetchSoilTypes = async () => {
    try {
      const data = await getSoilTypes();
      setSoilTypes(data as []);
    } catch (err) {
      console.error("Error in fetching soil", err);
    }
  };

  const findSoilNameUsingId = (soilId: any) => {
    const reqSoilObj = soilTypes.filter(
      (soil) => soil.id === soilId
    );
    return reqSoilObj[0]?.name;
  };

  function setSoilType(item: any) {
    editableData[selectedMoistureForModal]['soilType'] = item.id;
    setShowSelectSoilTypeModal(false);
  }

  const handleCheckSIMStatus = async () => {
    setIsLoadingSIM(true);
    try {
      const response = await getSIMDetails(device.imei);
      const data = await response.json();
      setSimDetails(data);
      const rawStatus = data?.status || data?.state || null;
      const normalizedStatus = rawStatus === 'Suspend' ? 'Suspended' : rawStatus;
      setSimStatus(normalizedStatus);
      setShowSIMAlert(true);
    } catch (error) {
      setOnError('Failed to get SIM status');
    } finally {
      setIsLoadingSIM(false);
    }
  };

  const handleSIMAction = async (action: 'activate' | 'suspend') => {
    try {
      const response = action === 'activate'
        ? await activateSIM(device.imei)
        : await suspendSIM(device.imei);

      const payload = await response.json();

      if (payload?.status === 'failed') {
        const errorMsg = Object.values(payload)
          .filter((v): v is Record<string, any> => typeof v === 'object' && v !== null)
          .map((v) => v.ErrorMessage)
          .find(Boolean);
        setOnError(errorMsg || `Failed to ${action} SIM`);
        return;
      }

      setSimStatus(action === 'activate' ? 'Active' : 'Suspended');
      setOnSuccess(`SIM ${action}d successfully`);
      setShowSIMAlert(false);
    } catch (error) {
      setOnError(`Error ${action}ing SIM`);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton />
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Device Graphs</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonItem>
          <IonLabel position="stacked">IMEI</IonLabel>
          <IonInput
            readonly
            id="farmId"
            name="farmId"
            value={device.imei}
          ></IonInput>
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">CCID</IonLabel>
          <IonInput
            id="ccid"
            name="ccid"
            value={ccid}
            onIonChange={(e) => setCcid(e.detail.value ?? '')}
            placeholder="Enter CCID"
          ></IonInput>
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Sensor ID</IonLabel>
          <IonInput
            id="sensorId"
            name="sensorId"
            value={sensorId}
            onIonChange={(e) => setSensorId(e.detail.value ?? '')}
            placeholder="Enter Sensor ID"
          ></IonInput>
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Plot ID</IonLabel>
          <IonInput
            id="plotId"
            name="plotId"
            readonly
            value={deviceData?.plotId}
            placeholder="Plot ID"
          ></IonInput>
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Batch ID</IonLabel>
          <IonInput
            id="batchId"
            name="batchId"
            readonly
            value={deviceData?.batchId}
            placeholder="Batch ID"
          ></IonInput>
        </IonItem>

        <TimeRangeSelector
          value={timeRange}
          onApply={setTimeRange}
          defaultSinceHours={240}
          idPrefix="device-graphs"
        />

        <IonGrid style={{ padding: '16px' }}>
          <IonRow>
            <IonCol size="6">
              <IonButton
                size="small"
                expand="block"
                color="primary"
                onClick={updateDevice}
                disabled={!ccid.trim()}
              >
                Update CCID
              </IonButton>
            </IonCol>
            <IonCol size="6">
              <IonButton
                size="small"
                expand="block"
                color="secondary"
                onClick={updateDeviceSensorId}
                disabled={!sensorId.trim()}
              >
                Update Sensor ID
              </IonButton>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="6">
              <IonButton
                size="small"
                expand="block"
                color="tertiary"
                onClick={handleOpenModal}
              >
                Calibration List
              </IonButton>
            </IonCol>
            <IonCol size="6">
              <IonButton
                size="small"
                expand="block"
                color="warning"
                onClick={handleCheckSIMStatus}
                disabled={isLoadingSIM}
              >
                {isLoadingSIM ? 'Checking...' : 'Check SIM Status'}
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>

        {isLoading ? (
          <div
            style={{
              width: "300px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "auto",
              paddingTop: "20px",
            }}
          >
            <IonSpinner />
          </div>
        ) : (
          <IonList>
            {(deviceGraphs?.length ?? 0) >= FINAL_FIELD_DATA_LIMIT && (
              <IonItem lines="none">
                <IonNote color="warning" className="ion-text-wrap">
                  Showing first {FINAL_FIELD_DATA_LIMIT} points only — recent data may be
                  missing. Narrow the range for complete data.
                </IonNote>
              </IonItem>
            )}
            {fields.length > 0 ? (
              fields.map((field: any, index: any) => (
                <IonCard key={`device-graph-${index}-${field}`}>
                  <IonCardHeader>
                    <IonCardSubtitle>{field}</IonCardSubtitle>
                  </IonCardHeader>
                  <TimeSeriesChart
                    format={"date"}
                    chartData={getChartData(deviceGraphs ?? [], field)}
                  />
                </IonCard>
              ))
            ) : (
              <div
                style={{
                  width: "300px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "auto",
                  paddingTop: "20px",
                }}
              >
                <IonLabel>No Graph Found</IonLabel>
              </div>
            )}
          </IonList>
        )}

        <IonModal
          isOpen={isModalOpen}
          onDidDismiss={() => setIsModalOpen(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Calibration List</IonTitle>
              <IonButton
                slot="end"
                fill="clear"
                onClick={() => setIsModalOpen(false)}
              >
                Close
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {['NERO_PULSE_UNIT', 'NERO_PULSE_LITE_UNIT'].includes(deviceData?.deviceType) ?
              <IonList style={{ marginBottom: "100px" }}>
                {Object.entries(editablePulseData).map(
                  ([field, value]) => (
                    <IonItem key={field} lines="none">
                      <IonGrid >
                        <IonRow
                          key={field}
                          className="ion-align-items-center ion-margin-vertical"
                          style={{ width: "100%" }}
                        >
                          <IonCol size="4">
                            <IonLabel>{field}</IonLabel>
                          </IonCol>
                          <IonCol size="6">
                            <IonItem
                              lines="full"
                              style={{
                                border: "1px solid #ccc",
                                borderRadius: "15px",
                                padding: "0 10px",
                              }}
                            >
                              <IonInput
                                type="number"
                                value={value ?? ""}
                                onIonChange={(e) =>
                                  handlePulseFieldChange(
                                    field,
                                    e.detail.value as string
                                  )
                                }
                                style={{ width: "100%" }}
                              />
                            </IonItem>
                          </IonCol>
                          <IonCol size="2">
                            <IonButton
                              fill="clear"
                              color="danger"
                              onClick={() => handlePulseClearField(field)}
                              style={{ padding: "0", height: "100%" }}
                            >
                              <IonIcon icon={closeCircleOutline} />
                            </IonButton>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonItem>
                  )
                )}
              </IonList>
              :
              <IonList style={{ marginBottom: "100px" }}>
                {Object.entries(editableData).map(
                  ([moisture, Calibrationfields]) => (
                    <IonItem key={moisture} lines="none">
                      <IonLabel position="stacked">
                        <IonCardSubtitle>{moisture}</IonCardSubtitle>
                      </IonLabel>
                      <IonGrid >
                        {Object.entries(Calibrationfields).map(([key, value]) => (
                          <IonRow
                            key={key}
                            className="ion-align-items-center ion-margin-vertical"
                            style={{ width: "100%" }}
                          >
                            <IonCol size="4">
                              <IonLabel>{key}</IonLabel>
                            </IonCol>
                            <IonCol size="6">
                              <IonItem
                                lines="full"
                                style={{
                                  border: "1px solid #ccc",
                                  borderRadius: "15px",
                                  padding: "0 10px",
                                }}
                              >
                                {key !== 'soilType'
                                  ?
                                  <IonInput
                                    type="number"
                                    value={value ?? ""}
                                    onIonChange={(e) =>
                                      handleFieldChange(
                                        moisture,
                                        key,
                                        e.detail.value as string
                                      )
                                    }
                                    style={{ width: "100%" }}
                                  />
                                  :
                                  <IonInput
                                    readonly
                                    id="soilTypeId"
                                    name="soilTypeId"
                                    value={value ? findSoilNameUsingId(value) : ''}
                                    placeholder="Select Soil Type"

                                    onClick={(e) => { setSelectedMoistureForModal(moisture); setShowSelectSoilTypeModal(true) }}
                                  ></IonInput>
                                }

                              </IonItem>
                            </IonCol>
                            <IonCol size="2">
                              <IonButton
                                fill="clear"
                                color="danger"
                                onClick={() => handleClearField(moisture, key)}
                                style={{ padding: "0", height: "100%" }}
                              >
                                <IonIcon icon={closeCircleOutline} />
                              </IonButton>
                            </IonCol>
                          </IonRow>
                        ))}
                      </IonGrid>
                    </IonItem>
                  )
                )}
              </IonList>
            }

            <IonButton
              className="floating-button"
              expand="block"
              color="primary"
              onClick={handleSave}
            >
              Save Changes
            </IonButton>
          </IonContent>
        </IonModal>


        {showSelectSoilTypeModal && (
          <IonModal id="soil-modal" isOpen={showSelectSoilTypeModal}>
            <SelectItem
              label="Select Soil Type"
              items={soilTypes}
              hideSearchbar={true}
              selectedValue={''}
              labelKey="name"
              valueKey="soilTypeId"
              displayType="card"

              setItem={setSoilType}
              onClose={setShowSelectSoilTypeModal}
            >
            </SelectItem>
          </IonModal>
        )}

        <IonAlert
          isOpen={showSIMAlert}
          onDidDismiss={() => setShowSIMAlert(false)}
          header="SIM Status"
          message={[
            `Current SIM status: ${simStatus}`,
            simDetails?.provider ? `Provider: ${simDetails.provider.toUpperCase()}` : null,
            simDetails?.iccid ? `ICCID: ${simDetails.iccid}` : null,
            simDetails?.msisdn ? `MSISDN: ${simDetails.msisdn}` : null,
          ].filter(Boolean).join('<br>')}
          buttons={[
            {
              text: 'Close',
              role: 'cancel',
              cssClass: 'secondary'
            },
            ...(simStatus === 'Active' ? [{
              text: 'Suspend',
              handler: () => handleSIMAction('suspend')
            }] : []),
            ...(simStatus === 'Suspended' ? [{
              text: 'Activate',
              handler: () => handleSIMAction('activate')
            }] : [])
          ]}
        />

        <IonToast
          isOpen={!!onSuccess}
          onDidDismiss={() => setOnSuccess("")}
          message={onSuccess}
          duration={2000}
          color="success"
        />

        <IonToast
          isOpen={!!onError}
          onDidDismiss={() => setOnError("")}
          message={onError}
          duration={2000}
          color="danger"
        />

      </IonContent>
    </IonPage>
  );
};

export default DeviceGraphs;
